<template>
  <section
    v-if="product"
    class="product-packaging"
    aria-labelledby="product-packaging-title"
  >
    <div class="product-packaging__copy">
      <h2 id="product-packaging-title">What comes with this pair?</h2>
      <p>
        Every pair of glasses comes in full retail packaging, including a
        branded case, cleaning cloth, paperwork, and other accessories, which
        may vary depending on the brand and collection.
      </p>
    </div>

    <button
      ref="questionTrigger"
      type="button"
      class="product-packaging__trigger"
      :aria-expanded="formOpen"
      aria-controls="product-packaging-question"
      @click="toggleForm"
    >
      Prepare a question for Hooligans
    </button>

    <div
      class="product-packaging__reveal"
      :class="{ 'product-packaging__reveal--open': formOpen }"
      :inert="formOpen ? undefined : true"
      :aria-hidden="formOpen ? undefined : 'true'"
    >
      <div class="product-packaging__reveal-inner">
        <form
          id="product-packaging-question"
          class="product-packaging__form"
          novalidate
          @submit.prevent="submitQuestion"
        >
          <p class="product-packaging__reply-note">
            Your reply email lets Hooligans reply to your question. This goes to
            support@hooliganseyewear.com.
          </p>
          <button
            type="button"
            class="product-packaging__close"
            :disabled="submitting"
            @click="closeForm"
          >
            Close question form
          </button>
          <input
            v-model="website"
            class="product-packaging__honeypot"
            type="text"
            name="website"
            tabindex="-1"
            autocomplete="off"
            aria-hidden="true"
          />

          <label for="product-packaging-email">Reply email</label>
          <input
            id="product-packaging-email"
            ref="emailInput"
            v-model="replyEmail"
            type="email"
            name="email"
            autocomplete="email"
            inputmode="email"
            required
            maxlength="254"
            :aria-invalid="Boolean(emailError)"
            :aria-describedby="
              emailError ? 'product-packaging-email-error' : undefined
            "
            @input="emailError = ''"
          />
          <p
            v-if="emailError"
            id="product-packaging-email-error"
            class="product-packaging__error"
            role="alert"
          >
            {{ emailError }}
          </p>

          <label for="product-packaging-message">Your question</label>
          <textarea
            id="product-packaging-message"
            ref="messageInput"
            v-model="message"
            name="message"
            required
            maxlength="2400"
            rows="7"
            :aria-invalid="Boolean(messageError)"
            :aria-describedby="
              messageError ? 'product-packaging-message-error' : undefined
            "
            @input="messageError = ''"
          />
          <p
            v-if="messageError"
            id="product-packaging-message-error"
            class="product-packaging__error"
            role="alert"
          >
            {{ messageError }}
          </p>

          <p v-if="requestError" class="product-packaging__error" role="alert">
            {{ requestError }}
          </p>
          <p class="product-packaging__status" role="status" aria-live="polite">
            {{ statusMessage }}
          </p>
          <button
            type="submit"
            class="product-packaging__send"
            :disabled="submitting"
          >
            {{ submitting ? "Sending question…" : "Send question" }}
          </button>
        </form>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { getProductPresentation } from "../../../../../utils/productPresentation";

const props = defineProps({ product: { type: Object, default: () => null } });

const formOpen = ref(false);
const questionTrigger = ref<HTMLButtonElement | null>(null);
const emailInput = ref<HTMLInputElement | null>(null);
const messageInput = ref<HTMLTextAreaElement | null>(null);
const replyEmail = ref("");
const message = ref("");
const website = ref("");
const emailError = ref("");
const messageError = ref("");
const requestError = ref("");
const statusMessage = ref("");
const submitting = ref(false);
const MAX_REPLY_EMAIL_BYTES = 254;
const MAX_CUSTOMER_MESSAGE_BYTES = 2400;
let requestVersion = 0;

const presentation = computed(() => getProductPresentation(props.product));
const productSignature = computed(() =>
  JSON.stringify({
    id: props.product?.id,
    name: props.product?.name,
    model: props.product?.model,
    sku: props.product?.sku,
    attributes: props.product?.attributes,
  })
);

function fact(label: "Model" | "Colour" | "Size") {
  return (
    presentation.value.identity.find((item) => item.label === label)?.value ||
    "Not listed"
  );
}

function draftMessage() {
  const name = String(props.product?.name || "this pair").trim() || "this pair";
  const sku = String(props.product?.sku ?? "").trim() || "Not listed";
  return `Hi Hooligans, could you confirm the packaging and accessories included with ${name}?\nModel: ${fact(
    "Model"
  )}; Colour: ${fact("Colour")}; Size: ${fact(
    "Size"
  )}; SKU: ${sku}\nIf available, could you share a photo of what comes with this pair?`;
}

function resetForProduct() {
  requestVersion += 1;
  submitting.value = false;
  message.value = draftMessage();
  website.value = "";
  emailError.value = "";
  messageError.value = "";
  requestError.value = "";
  statusMessage.value = "";
}

watch(productSignature, resetForProduct, { immediate: true });

function closeForm() {
  if (!submitting.value) {
    questionTrigger.value?.focus({ preventScroll: true });
    formOpen.value = false;
  }
}

function toggleForm() {
  if (formOpen.value) closeForm();
  else formOpen.value = true;
}

function utf8ByteLength(value: string) {
  return new TextEncoder().encode(value).length;
}

function normalizedQuestion() {
  return message.value.replace(/\r\n?/g, "\n").trim();
}

function validate() {
  emailError.value = "";
  messageError.value = "";
  requestError.value = "";
  const email = replyEmail.value.trim();
  const question = normalizedQuestion();
  if (
    !email ||
    /[\u0000-\u001f\u007f]/.test(email) ||
    /\s/.test(email) ||
    !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)
  ) {
    emailError.value = "Enter a valid reply email.";
    emailInput.value?.focus();
    return false;
  }
  if (utf8ByteLength(email) > MAX_REPLY_EMAIL_BYTES) {
    emailError.value = "Reply email must be 254 bytes or fewer.";
    emailInput.value?.focus();
    return false;
  }
  if (question.length < 10) {
    messageError.value = "Write a question of at least 10 characters.";
    messageInput.value?.focus();
    return false;
  }
  if (/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/.test(question)) {
    messageError.value = "Your question contains unsupported characters.";
    messageInput.value?.focus();
    return false;
  }
  if (utf8ByteLength(question) > MAX_CUSTOMER_MESSAGE_BYTES) {
    messageError.value = "Your question must be 2,400 bytes or fewer.";
    messageInput.value?.focus();
    return false;
  }
  return true;
}

async function submitQuestion() {
  if (!validate() || submitting.value) return;
  const version = ++requestVersion;
  submitting.value = true;
  statusMessage.value = "";
  try {
    const response = await fetch("/api/product-question", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({
        productId: props.product?.id,
        email: replyEmail.value.trim(),
        message: normalizedQuestion(),
        website: website.value,
      }),
    });
    const result = await response.json().catch(() => null);
    if (version !== requestVersion) return;
    if (!response.ok || result?.status !== true) {
      requestError.value =
        response.status === 429 || result?.error === "too_many_requests"
          ? "You’ve sent several questions recently. Please wait before trying again."
          : "We couldn’t send your question. Please try again.";
      return;
    }
    statusMessage.value = "Your question has been sent to Hooligans.";
  } catch {
    if (version === requestVersion) {
      requestError.value = "We couldn’t send your question. Please try again.";
    }
  } finally {
    if (version === requestVersion) submitting.value = false;
  }
}
</script>

<style scoped lang="scss">
.product-packaging__reveal {
  display: grid;
  grid-template-rows: 0fr;
  opacity: 0;
  visibility: hidden;
  transition: grid-template-rows var(--vf-panel-exit) var(--vf-panel-ease),
    opacity var(--vf-panel-exit) var(--vf-backdrop-ease),
    visibility 0s linear var(--vf-panel-exit);
}

.product-packaging__reveal--open {
  grid-template-rows: 1fr;
  opacity: 1;
  visibility: visible;
  transition-duration: var(--vf-panel-enter), var(--vf-panel-enter), 0s;
  transition-delay: 0s;
}

.product-packaging__reveal-inner {
  min-height: 0;
  overflow: hidden;
}

@media (prefers-reduced-motion: reduce) {
  .product-packaging__reveal {
    transition-property: opacity, visibility;
    transition-duration: var(--vf-panel-enter), 0s;
  }
}

.product-packaging {
  margin-top: 32px;
  padding: 28px 32px;
  border: 1px solid #e7e4df;
  background: #faf9f7;
  color: #101820;
}

.product-packaging__copy {
  max-width: 680px;

  h2 {
    margin: 0 0 10px;
    font-family: var(--vf-font-display);
    font-size: clamp(24px, 2.2vw, 31px);
    font-weight: 400;
    line-height: 1.22;
  }

  p {
    margin: 0;
    color: #444b51;
    font-size: 15px;
    line-height: 1.65;
  }
}

@media (min-width: 1024px) {
  .product-packaging {
    display: grid;
    grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr);
    align-items: center;
    gap: 0 48px;
  }

  .product-packaging__trigger {
    justify-self: start;
  }

  .product-packaging__reveal {
    grid-column: 2;
    width: 100%;
  }
}

.product-packaging__trigger,
.product-packaging__send {
  min-height: 48px;
  padding: 12px 20px;
  border: 1px solid #101820;
  background: #101820;
  color: #fff;
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #28323b;
  }

  &:disabled {
    cursor: progress;
    opacity: 0.7;
  }
}

.product-packaging__trigger {
  margin-top: 20px;
}

.product-packaging__close {
  justify-self: start;
  min-height: 36px;
  margin: 0 0 4px;
  padding: 4px 0;
  border: 0;
  background: transparent;
  color: #101820;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 4px;
  cursor: pointer;

  &:disabled {
    cursor: progress;
    opacity: 0.7;
  }
}

.product-packaging__form {
  display: grid;
  max-width: 680px;
  gap: 8px;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #ddd5cc;

  label {
    margin-top: 8px;
    font-size: 14px;
    font-weight: 600;
  }

  input:not(.product-packaging__honeypot),
  textarea {
    width: 100%;
    border: 1px solid #aeb2b5;
    border-radius: 0;
    padding: 12px;
    background: #fff;
    color: #101820;
    font: inherit;
    font-size: 16px;
    line-height: 1.5;
  }

  textarea {
    min-height: 168px;
    resize: vertical;
  }

  input[aria-invalid="true"],
  textarea[aria-invalid="true"] {
    border-color: #b3261e;
  }
}

.product-packaging__reply-note,
.product-packaging__status,
.product-packaging__error {
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
}

.product-packaging__reply-note {
  color: #444b51;
}

.product-packaging__status {
  min-height: 21px;
  color: #275d39;
}

.product-packaging__error {
  color: #b3261e;
}

.product-packaging__send {
  justify-self: start;
  margin-top: 8px;
}

.product-packaging__honeypot {
  position: absolute;
  box-sizing: border-box;
  width: 1px;
  min-width: 0;
  max-width: 1px;
  height: 1px;
  min-height: 0;
  max-height: 1px;
  margin: 0;
  padding: 0;
  border: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

.product-packaging :is(button, input, textarea):focus-visible {
  outline: 2px solid #101820;
  outline-offset: 3px;
}

@media (max-width: 767px) {
  .product-packaging {
    margin-top: 24px;
    padding: 24px 16px;
  }

  .product-packaging__copy h2 {
    font-size: 25px;
  }

  .product-packaging__trigger,
  .product-packaging__send {
    width: 100%;
  }

  .product-packaging__form textarea {
    min-height: 192px;
  }
}
</style>
